/**
 * SOFT_DELETE_STANDARD (F4-A) — local-first trash per entity type.
 *
 * Curated registry of the types that have trash in the UI (the soft-delete
 * tables synced with a real deletion flow in the app). Each entry knows how to
 * query its deleted rows and extract a human label. Restore/purge
 * live in `./mutations` and go through PowerSync's normal write-path.
 */
import type { SQLiteColumn } from "drizzle-orm/sqlite-core";
import {
  announcement,
  cohort,
  course_module,
  desc,
  forum_thread,
  isNotNull,
  type PowerSyncDrizzleDB,
  program,
  resource,
  schedule_slot,
} from "@space/powersync";

export const TRASH_KINDS = [
  "cohort",
  "course_module",
  "announcement",
  "forum_thread",
  "schedule_slot",
  "program",
  "resource",
] as const;

export type TrashKind = (typeof TRASH_KINDS)[number];

/** Trash row normalized for the UI. */
export interface TrashedItem {
  kind: TrashKind;
  id: string;
  /** Human label (title/name); null if the entity doesn't have one. */
  label: string | null;
  /** ISO timestamp of the deletion (for "deleted X ago" and the purge countdown). */
  deletedAt: string | null;
}

export const trashQueryKeys = {
  all: ["trash"] as const,
  byKind: (userId: string | undefined, kind: TrashKind) => ["trash", userId, kind] as const,
  byKindCount: (userId: string | undefined, kind: TrashKind) =>
    ["trash", userId, kind, "count"] as const,
};

/** Drizzle table for each kind (for generic restore/purge). */
export const TRASH_TABLES = {
  cohort,
  course_module,
  announcement,
  forum_thread,
  schedule_slot,
  program,
  resource,
} as const;

/**
 * `organization_id` column for each kind, for the isolation `scope` of
 * `restoreTrashedMutation`/`trashAgainMutation` (`./mutations`).
 *
 * `course_module` is left out on purpose: it doesn't have that column (see
 * `schema.ts`) — a course module inherits the course's isolation, not its
 * own organization's.
 *
 * `forum_thread` and `resource` are ALSO left out on purpose, even though they DO
 * have the column: they're created from authoring (`course-editor.tsx`,
 * `addForumModuleMutation`/`addResourceModuleMutation`) without `organizationId`
 * — the column stays NULL along that path (confirmed by reading the `insert`, not
 * hypothetical) and a `scope` by active org would never match, breaking
 * restore/re-delete for EVERY thread or resource in trash. See the twin note
 * in `academy/community/mutations.ts` (`updateThreadMutation`) and
 * `academy/modules/mutations.ts` (`updateResourceMutation`).
 */
export const TRASH_ORG_COLUMN: Partial<Record<TrashKind, SQLiteColumn>> = {
  cohort: cohort.organization_id,
  announcement: announcement.organization_id,
  schedule_slot: schedule_slot.organization_id,
  program: program.organization_id,
};

/** Query for deleted rows of a kind (most recent first). */
export function getTrashedQuery(db: PowerSyncDrizzleDB, kind: TrashKind) {
  const table = TRASH_TABLES[kind];
  return db.select().from(table).where(isNotNull(table.deleted_at)).orderBy(desc(table.deleted_at));
}

/** Human label for a raw trash row, by kind. */
export function trashedLabel(kind: TrashKind, row: Record<string, unknown>): string | null {
  const str = (key: string): string | null => {
    const v = row[key];
    return typeof v === "string" && v.length > 0 ? v : null;
  };
  switch (kind) {
    case "cohort":
    case "program": {
      return str("name");
    }
    case "announcement":
    case "forum_thread":
    case "resource": {
      return str("title");
    }
    case "course_module": {
      return str("kind");
    }
    case "schedule_slot": {
      const start = str("start_time");
      const end = str("end_time");
      return start && end ? `${start}–${end}` : start;
    }
    default: {
      return null;
    }
  }
}

/** Days remaining until physical purge (server: TRASH_PURGE_DAYS, default 30). */
export const DEFAULT_TRASH_PURGE_DAYS = 30;

export function daysUntilPurge(
  deletedAt: string | null,
  purgeDays: number = DEFAULT_TRASH_PURGE_DAYS,
  now: number = Date.now(),
): number | null {
  if (!deletedAt) {
    return null;
  }
  const deleted = Date.parse(deletedAt);
  if (Number.isNaN(deleted)) {
    return null;
  }
  const msLeft = deleted + purgeDays * 24 * 60 * 60 * 1000 - now;
  return Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
}
